'use client'

import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useMemo,
} from 'react'
import { ForecastData } from '@/app/service/models/performanceMetrics'

export interface BannerGoalPayload {
	readonly selectedYear?: number
	readonly forecastNumberValue?: ForecastData | null
}

interface BannerAndGoalContextType {
	openGoalDialog: boolean
	openGoalBanner?: boolean | null
	openBannerDismissDialog?: boolean
	openGoalDialogFromBanner?: boolean | null
	setOpenGoalDialog: (isOpen: boolean) => void
	goalDialogCloseHandler: () => void
	goalDialogOpenHandler: () => void
	openGoalDialogFromBannerHandler: () => void
	openGoalDialogFromGoalSettingButtonHandler: () => void
	setOpenBannerDismissDialog?: (isOpen: boolean) => void
	setOpenGoalDialogFromBanner?: (isOpen: boolean) => void
	setOpenGoalBanner?: (isOpen: boolean) => void
	bannerDismissDialogCloseHandler: () => void
	bannerDismissDialogOpenHandler: () => void
	bannerCloseHandler?: () => void
	bannerOpenHandler?: () => void
	openGoalDialogWithPayload: (payload: BannerGoalPayload) => void
}

const BannerAndGoalContext = createContext<
	BannerAndGoalContextType | undefined
>(undefined)

export const BannerAndGoalProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [openGoalDialog, setOpenGoalDialog] = useState(false)
	const [openGoalDialogFromBanner, setOpenGoalDialogFromBanner] = useState<
		boolean | null
	>(null)
	// Add state to hold optional payload coming from banner CTA
	const [bannerGoalPayload, setBannerGoalPayload] =
		React.useState<BannerGoalPayload | null>(null)

	// New helper to open the goal dialog and carry payload
	const openGoalDialogWithPayload = (payload: BannerGoalPayload) => {
		setBannerGoalPayload(payload)
		goalDialogOpenHandler()
	}

	// Clear payload on close (ensure dialog close clears payload)
	const wrappedGoalDialogCloseHandler = () => {
		setBannerGoalPayload(null)
		goalDialogCloseHandler()
	}

	const openGoalDialogFromBannerHandler = React.useCallback(() => {
		setOpenGoalDialogFromBanner(true)
	}, [])

	const openGoalDialogFromGoalSettingButtonHandler = React.useCallback(() => {
		setOpenGoalDialogFromBanner(false)
	}, [])

	const goalDialogCloseHandler = React.useCallback(() => {
		setOpenGoalDialog(false)
	}, [])

	const goalDialogOpenHandler = React.useCallback(() => {
		setOpenGoalDialog(true)
	}, [])

	const [openBannerDismissDialog, setOpenBannerDismissDialog] = useState(false)

	const bannerDismissDialogCloseHandler = React.useCallback(() => {
		setOpenBannerDismissDialog(false)
	}, [])
	const bannerDismissDialogOpenHandler = React.useCallback(() => {
		setOpenBannerDismissDialog(true)
	}, [])

	const [openGoalBanner, setOpenGoalBanner] = useState(true)
	const bannerCloseHandler = React.useCallback(() => {
		setOpenGoalBanner(false)
	}, [])
	const bannerOpenHandler = React.useCallback(() => {
		setOpenGoalBanner(true)
	}, [])

	// Move hooks to the top level

	const contextValue = useMemo(
		() => ({
			openGoalDialog,
			setOpenGoalDialog,
			openGoalDialogFromBanner,
			setOpenGoalDialogFromBanner,
			openGoalDialogFromBannerHandler,
			openGoalDialogFromGoalSettingButtonHandler,
			goalDialogCloseHandler: wrappedGoalDialogCloseHandler,
			goalDialogOpenHandler,
			openBannerDismissDialog,
			setOpenBannerDismissDialog,
			bannerDismissDialogCloseHandler,
			bannerDismissDialogOpenHandler,
			openGoalBanner,
			setOpenGoalBanner,
			bannerCloseHandler,
			bannerOpenHandler,
			bannerGoalPayload,
			openGoalDialogWithPayload,
		}),
		[
			openGoalDialog,
			openGoalDialogFromBanner,
			openGoalDialogFromBannerHandler,
			openGoalDialogFromGoalSettingButtonHandler,
			wrappedGoalDialogCloseHandler,
			goalDialogOpenHandler,
			openBannerDismissDialog,
			bannerDismissDialogCloseHandler,
			bannerDismissDialogOpenHandler,
			openGoalBanner,
			bannerCloseHandler,
			bannerOpenHandler,
			bannerGoalPayload,
			openGoalDialogWithPayload,
		]
	)

	return (
		<BannerAndGoalContext.Provider value={contextValue}>
			{children}
		</BannerAndGoalContext.Provider>
	)
}

export const useBannerAndGoalContext = (): BannerAndGoalContextType => {
	const context = useContext(BannerAndGoalContext)
	if (!context) {
		throw new Error(
			'useBannerAndGoalContext must be used within a BannerAndGoalProvider'
		)
	}
	return context
}
